/**
 * API Client for CoachSight Backend
 *
 * Provides a typed interface to the backend API with:
 * - Centralized base URL and JWT authentication
 * - Query parameters and JSON body support
 * - Abort signal support for cancellation
 * - Normalized error handling
 * - Automatic 401 logout on unauthorized
 */

import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import { useAuthStore } from '@/stores/auth'

export interface ApiErrorResponse {
  status: number
  message: string
  code?: string
  details?: Record<string, unknown>
  error?: {
    code?: string
    message?: string
    details?: Record<string, unknown>
  }
}

export interface ApiError extends Error {
  status?: number
  code?: string
  details?: Record<string, unknown>
}

export class ApiClient {
  private client: AxiosInstance

  constructor(baseURL: string = import.meta.env.VITE_CSAPI_URL || '') {
    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor: Add JWT token
    this.client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Response interceptor: Handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Clear auth and trigger logout
          localStorage.removeItem('token')
          const authStore = useAuthStore()
          authStore.logout()
        }
        return Promise.reject(this.normalizeError(error))
      }
    )
  }

  async get<T = unknown>(
    path: string,
    query?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(path, {
      params: query,
      signal,
    })
    return response.data
  }

  async post<T = unknown>(
    path: string,
    body?: unknown,
    query?: Record<string, unknown>,
    signal?: AbortSignal,
    timeoutMs?: number
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(path, body, {
      params: query,
      signal,
      ...(timeoutMs ? { timeout: timeoutMs } : {}),
    })
    return response.data
  }

  async put<T = unknown>(
    path: string,
    body?: unknown,
    query?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(path, body, {
      params: query,
      signal,
    })
    return response.data
  }

  async patch<T = unknown>(
    path: string,
    body?: unknown,
    query?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(path, body, {
      params: query,
      signal,
    })
    return response.data
  }

  async delete<T = unknown>(
    path: string,
    query?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(path, {
      params: query,
      signal,
    })
    return response.data
  }

  private normalizeError(error: AxiosError): ApiError {
    const apiError = new Error() as ApiError

    if (error.response) {
      const data = error.response.data as Partial<ApiErrorResponse>
      const responseError = data.error
      apiError.status = error.response.status
      apiError.message = responseError?.message || data.message || error.message
      apiError.code = responseError?.code || data.code
      apiError.details = responseError?.details || data.details
    } else if (error.request) {
      apiError.message = 'Network error: No response from server'
      apiError.code = 'NETWORK_ERROR'
    } else {
      apiError.message = error.message || 'Unknown error'
    }

    return apiError
  }
}

export const apiClient = new ApiClient()
